import { memo, useLayoutEffect, useRef } from "react";
import { MapMarker } from "../map.types";
import { useCustomMarkerStyles } from "../../../shared/hooks/useCustomMarkerStyles";

export type LocationMarkerProps = {
  locationMarker: MapMarker;
  setMarkerRef: (marker: google.maps.Marker | null, key: string) => void;
  onMarkerClick: (event: google.maps.MapMouseEvent, marker: MapMarker) => void;
};

/**
 * Wrapper Component for an Marker for a single marker.
 */
const MarkerWrapper = (props: LocationMarkerProps) => {
  const { locationMarker, setMarkerRef, onMarkerClick } = props;
  const googleMarkerRef = useRef<google.maps.Marker | null>(null);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const latestMarkerRef = useRef(locationMarker);
  const latestOnMarkerClickRef = useRef(onMarkerClick);

  const { customIcon } = useCustomMarkerStyles(locationMarker);
  const hasCoordinates =
    locationMarker.latitude !== undefined &&
    locationMarker.longitude !== undefined;

  latestMarkerRef.current = locationMarker;
  latestOnMarkerClickRef.current = onMarkerClick;

  useLayoutEffect(() => {
    if (!hasCoordinates) return undefined;

    const marker = new google.maps.Marker({
      clickable: true,
      position: {
        lat: locationMarker.latitude as number,
        lng: locationMarker.longitude as number,
      },
      zIndex: 1,
    });

    googleMarkerRef.current = marker;
    setMarkerRef(marker, locationMarker.id);

    clickListenerRef.current = marker.addListener(
      "click",
      (event: google.maps.MapMouseEvent) => {
        latestOnMarkerClickRef.current(event, latestMarkerRef.current);
      },
    );

    return () => {
      clickListenerRef.current?.remove();
      clickListenerRef.current = null;
      setMarkerRef(null, locationMarker.id);
      marker.setMap(null);
      googleMarkerRef.current = null;
    };
  }, [hasCoordinates, locationMarker.id, setMarkerRef]);

  useLayoutEffect(() => {
    const marker = googleMarkerRef.current;
    if (!marker || !hasCoordinates) return;

    marker.setPosition({
      lat: locationMarker.latitude as number,
      lng: locationMarker.longitude as number,
    });
    marker.setIcon(customIcon);
    marker.setZIndex(1);
  }, [
    customIcon,
    hasCoordinates,
    locationMarker,
    locationMarker.latitude,
    locationMarker.longitude,
  ]);

  return null;
};

// Optimized memo with custom comparison for maximum performance
const arePropsEqual = (
  prevProps: LocationMarkerProps,
  nextProps: LocationMarkerProps,
) => {
  const prev = prevProps.locationMarker;
  const next = nextProps.locationMarker;

  const propsEqual =
    prev.id === next.id &&
    prev.latitude === next.latitude &&
    prev.longitude === next.longitude &&
    prev.driverStatus === next.driverStatus &&
    prev.selected === next.selected &&
    prev.locationSequence === next.locationSequence &&
    prev.order?.status === next.order?.status &&
    prev.region?.color === next.region?.color &&
    prev.color === next.color &&
    prevProps.onMarkerClick === nextProps.onMarkerClick &&
    prevProps.setMarkerRef === nextProps.setMarkerRef;

  return propsEqual;
};

export default memo(MarkerWrapper, arePropsEqual);
